import { combineEpics, ofType } from 'redux-observable'
import { of } from 'rxjs'
import { catchError, filter, map, mergeMap, takeUntil } from 'rxjs/operators'

import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { FETCH_SELECTED_COMPANY_DATA_ASYNC } from 'containers/PartnerOptions/constants'
import { addToast } from 'containers/Toasts/actions'

import {
  cancelIntegrationError,
  cancelIntegrationSuccess,
  fetchSlackDataError,
  fetchSlackDataSuccess,
  fetchTeamsDataError,
  fetchTeamsDataSuccess,
  updateCollaborationAppAlertsError,
  updateCollaborationAppAlertsSuccess,
  updateNotificationsError,
  updateNotificationsSuccess,
} from './actions'
import {
  CANCEL_INTEGRATION_REQUEST,
  FETCH_SLACK_DATA_REQUEST,
  FETCH_TEAMS_DATA_REQUEST,
  SLACK_CONST,
  UPDATE_COLLABORATION_APP_ALERTS_REQUEST,
  UPDATE_NOTIFICATIONS_REQUEST,
} from './constants'

const fetchSlackDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_SLACK_DATA_REQUEST.INITIATE,
      UPDATE_NOTIFICATIONS_REQUEST.SUCCESS,
      CANCEL_INTEGRATION_REQUEST.SUCCESS,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.collaborationApps)
      return true
    }),
    mergeMap(() =>
      api.settings
        .getCollaborationAppsData(state$.value.partnerOptions.selectedCompany?.value, 'slack')
        .pipe(
          map((response) => fetchSlackDataSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchSlackDataError, error))),
          takeUntil(action$.pipe(ofType(FETCH_SLACK_DATA_REQUEST.CANCEL)))
        )
    )
  )

const fetchTeamsDataEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(
      FETCH_TEAMS_DATA_REQUEST.INITIATE,
      UPDATE_NOTIFICATIONS_REQUEST.SUCCESS,
      CANCEL_INTEGRATION_REQUEST.SUCCESS,
      FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS
    ),
    filter((action) => {
      if (action.type === FETCH_SELECTED_COMPANY_DATA_ASYNC.SUCCESS)
        return matchURL(URL_CONST.settings.collaborationApps)
      return true
    }),
    mergeMap(() =>
      api.settings
        .getCollaborationAppsData(state$.value.partnerOptions.selectedCompany?.value, 'teams')
        .pipe(
          map((response) => fetchTeamsDataSuccess(response)),
          catchError((error) => of(epicsErrorHandler(fetchTeamsDataError, error))),
          takeUntil(action$.pipe(ofType(FETCH_TEAMS_DATA_REQUEST.CANCEL)))
        )
    )
  )

const updateNotificationsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_NOTIFICATIONS_REQUEST.INITIATE),
    mergeMap(() =>
      api.settings
        .updateCollaborationAppsData(
          state$.value.partnerOptions.selectedCompany?.value,
          state$.value.settings.notifications.collaborationApps.chosenIntegration === SLACK_CONST
            ? 'slack'
            : 'teams',
          {
            webhookUrl: state$.value.settings.notifications.collaborationApps.webhookUrl,
          }
        )
        .pipe(
          map((response) => updateNotificationsSuccess(response)),
          catchError((error) => of(epicsErrorHandler(updateNotificationsError, error))),
          takeUntil(action$.pipe(ofType(UPDATE_NOTIFICATIONS_REQUEST.CANCEL)))
        )
    )
  )

const updateCollaborationAppAlertsEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(UPDATE_COLLABORATION_APP_ALERTS_REQUEST.INITIATE),
    mergeMap((action) =>
      api.settings
        .updateCollaborationAppsAlerts(
          state$.value.partnerOptions.selectedCompany?.value,
          state$.value.settings.notifications.collaborationApps.chosenIntegration === SLACK_CONST
            ? 'slack'
            : 'teams',
          { alerts: action.payload.alerts }
        )
        .pipe(
          mergeMap((response) =>
            of(
              updateCollaborationAppAlertsSuccess(response),
              addToast({
                message: 'Changes for notifications were saved successfully!',
              })
            )
          ),
          catchError((error) =>
            of(
              epicsErrorHandler(updateCollaborationAppAlertsError, error),
              addToast({
                message:
                  'Something went wrong. Changes for notifications were not saved. Please contact support.',
                type: 'error',
              })
            )
          ),
          takeUntil(action$.pipe(ofType(UPDATE_COLLABORATION_APP_ALERTS_REQUEST.CANCEL)))
        )
    )
  )

const cancelIntegrationEpic = (action$, state$, { api, epicsErrorHandler }) =>
  action$.pipe(
    ofType(CANCEL_INTEGRATION_REQUEST.INITIATE),
    mergeMap(() =>
      api.settings
        .cancelCollaborationAppsIntegration(
          state$.value.partnerOptions.selectedCompany?.value,
          state$.value.settings.notifications.collaborationApps.chosenIntegration === SLACK_CONST
            ? 'slack'
            : 'teams'
        )
        .pipe(
          map((response) => cancelIntegrationSuccess(response)),
          catchError((error) => of(epicsErrorHandler(cancelIntegrationError, error))),
          takeUntil(action$.pipe(ofType(CANCEL_INTEGRATION_REQUEST.CANCEL)))
        )
    )
  )

export default combineEpics(
  fetchSlackDataEpic,
  fetchTeamsDataEpic,
  updateNotificationsEpic,
  cancelIntegrationEpic,
  updateCollaborationAppAlertsEpic
)
